import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { useSubscription } from '@/hooks/useSubscription';
import { SubscriptionGuard } from '@/components/ui/subscription-guard';
import { Cashpurr } from '@/components/ui/cashpurr';
import { supabase } from '@/integrations/supabase/client';
import { 
  BookOpen, Edit3, Save, Copy, Download, 
  MessageSquare, Target, DollarSign, Users, 
  Mail, Globe, TrendingUp, Eye
} from 'lucide-react';

interface PlaybookTemplate {
  id: string;
  title: string;
  description: string;
  category: 'cold_outreach' | 'landing_page' | 'pricing' | 'social_media' | 'email_marketing';
  content: string;
  tags: string[];
}

interface UserPlaybook {
  id: string;
  user_id: string;
  template_id: string;
  title: string;
  content: string;
  category: string;
  is_customized: boolean;
  created_at: string;
  updated_at: string;
}

const PlaybooksContent: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { subscriptionInfo } = useSubscription();
  useNotifications();
  
  const [templates, setTemplates] = useState<PlaybookTemplate[]>([]);
  const [userPlaybooks, setUserPlaybooks] = useState<UserPlaybook[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<PlaybookTemplate | null>(null);
  const [editingPlaybook, setEditingPlaybook] = useState<UserPlaybook | null>(null);
  const [customTitle, setCustomTitle] = useState('');
  const [customContent, setCustomContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'templates' | 'my-playbooks'>('templates');

  useEffect(() => {
    if (user) {
      loadTemplates();
      loadUserPlaybooks();
    }
  }, [user]);

  const loadTemplates = () => {
    // Predefined templates - keep existing implementation
    const defaultTemplates: PlaybookTemplate[] = [
      {
        id: 'cold-outreach-1',
        title: 'Cold Outreach DMs',
        description: 'Professional yet personal cold outreach messages for social media',
        category: 'cold_outreach',
        content: `Hi [Name]! 👋

I noticed you're [specific observation about their work/business] and I think you might be interested in [your solution].

[Brief value proposition - what problem you solve]

Would you be open to a quick 15-minute call to see if this could help you [specific benefit]?

Best,
[Your Name]`,
        tags: ['social media', 'networking', 'lead generation']
      },
      {
        id: 'landing-page-1',
        title: 'High-Converting Landing Page',
        description: 'Landing page copy that converts visitors into customers',
        category: 'landing_page',
        content: `🚀 [Compelling Headline That Solves Their Problem]

[Subheadline that expands on the benefit]

✅ [Benefit 1]
✅ [Benefit 2] 
✅ [Benefit 3]

[Social proof - "Join 500+ entrepreneurs who..."]

[Clear CTA button with action-oriented text]

[Risk reversal - "30-day money-back guarantee"]`,
        tags: ['conversion', 'copywriting', 'landing page']
      },
      {
        id: 'pricing-1',
        title: 'Value-Based Pricing Strategy',
        description: 'How to price your product based on customer value',
        category: 'pricing',
        content: `💰 Pricing Strategy Framework

1. **Value Calculation**
   - How much does the problem cost customers?
   - What's the ROI of your solution?
   - Time/money saved = Your price ceiling

2. **Competitive Analysis**
   - Research competitor pricing
   - Position yourself 20-30% above if better
   - Position yourself 20-30% below if newer

3. **Pricing Tiers**
   - Basic: Core features
   - Pro: Popular features + premium support
   - Enterprise: Everything + white-label

4. **Psychological Pricing**
   - $97 vs $100
   - Monthly vs annual (2 months free)
   - Early bird discounts`,
        tags: ['pricing', 'strategy', 'value']
      },
      {
        id: 'social-media-1',
        title: 'Social Media Content Calendar',
        description: 'Weekly content strategy for consistent social media presence',
        category: 'social_media',
        content: `📅 Weekly Content Strategy

**Monday**: Motivation/Inspiration
- Quote + personal story
- Behind-the-scenes
- Industry insights

**Tuesday**: Educational Content
- How-to tips
- Industry news
- Case studies

**Wednesday**: Community Engagement
- Ask questions
- Share user content
- Polls/surveys

**Thursday**: Value-Add Content
- Free resources
- Templates
- Tools recommendations

**Friday**: Fun/Personal
- Weekend plans
- Team highlights
- Industry humor

**Weekend**: User-generated content + engagement`,
        tags: ['social media', 'content', 'strategy']
      },
      {
        id: 'email-marketing-1',
        title: 'Email Sequence for New Subscribers',
        description: 'Welcome email sequence to convert subscribers into customers',
        category: 'email_marketing',
        content: `📧 Welcome Email Sequence

**Email 1 (Immediate)**: Welcome + What to Expect
- Thank you for subscribing
- What they'll receive
- First valuable resource

**Email 2 (Day 2)**: Your Story
- How you started
- Why you're passionate
- Personal connection

**Email 3 (Day 4)**: First Major Value
- In-depth resource
- Actionable tips
- Social proof

**Email 4 (Day 7)**: Engagement
- Ask a question
- Invite to reply
- Community building

**Email 5 (Day 10)**: Soft Sell
- Case study
- Success story
- Gentle CTA`,
        tags: ['email', 'automation', 'conversion']
      }
    ];
    
    setTemplates(defaultTemplates);
  };

  const loadUserPlaybooks = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_playbooks')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setUserPlaybooks(data as any || []);
    } catch (error) {
      console.error('Error loading user playbooks:', error);
    }
  };

  const selectTemplate = (template: PlaybookTemplate) => {
    setSelectedTemplate(template);
    setCustomTitle(template.title);
    setCustomContent(template.content);
    setEditingPlaybook(null);
  };

  const startCustomizing = () => {
    if (!selectedTemplate) return;
    
    setEditingPlaybook({
      id: '',
      user_id: user?.id || '',
      template_id: selectedTemplate.id,
      title: customTitle,
      content: customContent,
      category: selectedTemplate.category,
      is_customized: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  };

  const savePlaybook = async () => {
    if (!user || !editingPlaybook) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('user_playbooks')
        .upsert({
          user_id: user.id,
          playbook_id: editingPlaybook.template_id,
          customized_content: customContent,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;

      toast({
        title: "Playbook Saved! 🎉",
        description: "Your customized playbook has been saved.",
      });

      setEditingPlaybook(null);
      setSelectedTemplate(null);
      loadUserPlaybooks();
    } catch (error) {
      console.error('Error saving playbook:', error);
      toast({
        title: "Error",
        description: "Failed to save playbook. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied! 📋",
      description: "Content copied to clipboard.",
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'cold_outreach':
        return <MessageSquare className="w-4 h-4" />;
      case 'landing_page':
        return <Globe className="w-4 h-4" />;
      case 'pricing':
        return <DollarSign className="w-4 h-4" />;
      case 'social_media':
        return <TrendingUp className="w-4 h-4" />;
      case 'email_marketing':
        return <Mail className="w-4 h-4" />;
      default:
        return <BookOpen className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'cold_outreach':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'landing_page':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pricing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'social_media':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'email_marketing':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <Cashpurr size="lg" mood="excited" className="mx-auto mb-2" />
        <h1 className="text-3xl font-bold">Launch Playbooks</h1>
        <p className="text-muted-foreground">
          Proven templates and strategies to launch your business
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-muted p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('templates')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'templates'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Templates
        </button>
        <button
          onClick={() => setActiveTab('my-playbooks')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'my-playbooks'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          My Playbooks
        </button>
      </div>

      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card 
              key={template.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => selectTemplate(template)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {getCategoryIcon(template.category)}
                    <Badge className={getCategoryColor(template.category)}>
                      {template.category.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
                <CardTitle className="text-lg">{template.title}</CardTitle>
                <CardDescription>{template.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-1">
                    {template.tags.map((tag) => (
                      <span key={tag} className="text-xs bg-muted px-2 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectTemplate(template);
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'my-playbooks' && (
        <div className="space-y-4">
          {userPlaybooks.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Custom Playbooks Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start by customizing a template to create your first playbook.
                </p>
                <Button onClick={() => setActiveTab('templates')}>
                  Browse Templates
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {userPlaybooks.map((playbook) => (
                <Card key={playbook.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getCategoryIcon(playbook.category)}
                        <Badge className={getCategoryColor(playbook.category)}>
                          {playbook.category.replace('_', ' ')}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingPlaybook(playbook);
                          setCustomTitle(playbook.title);
                          setCustomContent(playbook.content);
                          setSelectedTemplate(null);
                        }}
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                    </div>
                    <CardTitle>{playbook.title}</CardTitle>
                    <CardDescription>
                      Last updated: {new Date(playbook.updated_at).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {playbook.content.substring(0, 150)}...
                      </p>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(playbook.content)}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingPlaybook(playbook);
                            setCustomTitle(playbook.title);
                            setCustomContent(playbook.content);
                            setSelectedTemplate(null);
                          }}
                        >
                          <Edit3 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Template Viewer/Editor */}
      {(selectedTemplate || editingPlaybook) && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {editingPlaybook ? 'Customize Playbook' : selectedTemplate?.title}
                </CardTitle>
                <CardDescription>
                  {editingPlaybook 
                    ? 'Make this template your own'
                    : 'Review and customize this template'
                  }
                </CardDescription>
              </div>
              <div className="flex space-x-2">
                <Button 
                  variant="outline"
                  onClick={() => copyToClipboard(customContent)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
                {selectedTemplate && !editingPlaybook && (
                  <Button onClick={startCustomizing}>
                    <Edit3 className="w-4 h-4 mr-2" />
                    Customize
                  </Button>
                )}
                {editingPlaybook && (
                  <Button onClick={savePlaybook} disabled={loading}>
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {editingPlaybook && (
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    placeholder="Enter playbook title..."
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="content">
                  {editingPlaybook ? 'Content' : 'Template Preview'}
                </Label>
                {editingPlaybook ? (
                  <Textarea
                    id="content"
                    value={customContent}
                    onChange={(e) => setCustomContent(e.target.value)}
                    className="min-h-[300px] font-mono text-sm"
                    placeholder="Customize your playbook content..."
                  />
                ) : (
                  <div className="p-4 bg-muted rounded-lg">
                    <pre className="whitespace-pre-wrap text-sm">
                      {selectedTemplate?.content}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default function PlaybooksPage() {
  return (
    <SubscriptionGuard feature="playbooks">
      <PlaybooksContent />
    </SubscriptionGuard>
  );
}
